import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFields,
  getNotificationItem,
  createNotification,
  updateNotification,
} from "store/outAppNotifications/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import { Modal } from "components/Modal/Modal";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import PushMainTab from "./tabs/push/PushMainTab";
import { PushLocalesTab } from "./tabs/push/PushLocalesTab";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { PushSendTab } from "./tabs/push/PushSendTab";
import { ModalNotificationTabs } from "configs/types";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import styles from "./NotificationsEditModal.module.scss";

function isValidRemoveAfterHours(value: unknown) {
  if (value === undefined || value === null || String(value).trim() === "") {
    return true;
  }
  const raw = String(value).trim();
  if (!/^[1-9]\d*$/.test(raw)) {
    return false;
  }
  const hours = Number(raw);
  return Number.isSafeInteger(hours) && hours > 0 && hours <= 2147483647;
}

const NotificationsEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const formErrors = useFormErrors();

  const isItemFetched = useAppSelector(
    (store) => store["out-notif"].isItemFetched,
  );
  const payload = useAppSelector((store) => store["out-notif"].payload);
  const isItemFetching = useAppSelector(
    (store) => store["out-notif"].isItemFetching,
  );

  const [activeTab, setActiveTab] = useState<ModalNotificationTabs>(
    ModalNotificationTabs.main,
  );

  const isAutoNotification = location.pathname.includes("auto");
  const isManualNotification = location.pathname.includes("manual");
  const tabs: ModalNotificationTabs[] = [
    ModalNotificationTabs.main,
    ModalNotificationTabs.locales,
    ...(isAutoNotification || isManualNotification
      ? [ModalNotificationTabs.send]
      : []),
  ];

  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
  const [isConfirmingDispatch, setIsConfirmingDispatch] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);

  const [activeLocale, setActiveLocale] = useState("");

  const [translation, setTranslation] = useState({
    locale: "",
    title: "",
    content: "",
    isActive: 0,
  });
  const [initialTranslation, setInitialTranslation] = useState({
    locale: "",
    title: "",
    content: "",
    isActive: 0,
  });

  const permissions = usePermissionHelper();

  useEffect(() => {}, [translation]);
  const [isTranslationSaving, setIsTranslationSaving] = useState(false);
  const [isLocalesLoading, setIsLocalesLoading] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      dispatch(getNotificationItem(searchParams.get("entityId") ?? ""));
    }
  }, [searchParams, isItemFetched]);

  useEffect(() => {
    if (!searchParams.get("entityId")) {
      if (location.pathname.includes("auto")) {
        handleData("type", "auto");
      }

      if (location.pathname.includes("manual")) {
        handleData("type", "manual");
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isAutoNotification || !entityId) {
      return;
    }

    let isMounted = true;

    instance
      .get(`${apiRoutes.notificationItem}/${entityId}/segments`)
      .then((result) => {
        if (isMounted) {
          setSelectedSegmentIds(result.data?.segmentIds ?? []);
        }
      })
      .catch((error) => {
        handleApiError({ error, t, dispatch });
      });

    return () => {
      isMounted = false;
    };
  }, [isAutoNotification, entityId]);

  const isEdit = Boolean(entityId || payload?.id);
  const canEdit = isEdit ? permissions?.update : permissions?.create;

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  const handleAttach = async () => {
    if (!permissions?.update) return;

    const notificationId = Number(entityId ?? payload?.id);
    if (!notificationId) {
      return;
    }

    setIsAttaching(true);

    try {
      const result = await instance.put(
        `${apiRoutes.notificationItem}/${notificationId}/segments`,
        { segmentIds: Array.from(new Set(selectedSegmentIds)) },
      );

      setSelectedSegmentIds(result.data?.segmentIds ?? []);
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({ error, t, dispatch });
    } finally {
      setIsAttaching(false);
    }
  };

  const handleApply = async () => {
    if (!canEdit) return;

    formErrors.resetErrors();
    if (!payload) {
      return;
    }

    if (activeTab === ModalNotificationTabs.send) {
      if (isAutoNotification) {
        await handleAttach();
        return;
      }
      setIsConfirmingDispatch(true);
      return;
    }

    if (activeTab === ModalNotificationTabs.locales) {
      if (!entityId) {
        return;
      }
      setIsTranslationSaving(true);

      try {
        const changes = getChangedFields(
          initialTranslation,
          translation,
          ["title", "content", "isActive"],
          {
            title: normalizeOptionalText,
            content: normalizeOptionalText,
            isActive: Boolean,
          },
        );
        await instance.put(
          `${apiRoutes.notificationSetTranslation}/${entityId}`,
          {
            locale: activeLocale,
            ...changes,
          },
        );
        setInitialTranslation(translation);

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        console.log(error);
      } finally {
        setIsTranslationSaving(false);
      }

      return;
    }

    if (!isValidRemoveAfterHours(payload.removeAfterHours)) {
      formErrors.setFieldError("removeAfterHours", "IsInt");
      return;
    }

    if (payload.type === "auto" && !payload.trigger) {
      formErrors.setFieldError("trigger", t("fieldRequired"));

      dispatch(
        showNotification({
          title: t("warning"),
          message: t("selectedTriggerForAutoEvent"),
          type: NotificationTypes.warning,
        }),
      );

      return;
    }

    try {
      if (searchParams.get("entityId")) {
        await dispatch(updateNotification(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        return;
      }

      const newId = await dispatch(createNotification(payload)).unwrap();

      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityCreateSuccess"),
          type: NotificationTypes.success,
        }),
      );

      navigate(
        `${location.pathname}?activeModal=notification&entityId=${newId}`,
      );
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const handleTabClick = (e: ModalNotificationTabs) => {
    if (!isEdit && e !== ModalNotificationTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setIsSendLoading(e === ModalNotificationTabs.send);
    setActiveTab(e);
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleDispatch = async () => {
    if (!permissions?.update) return;

    const notificationId = Number(entityId ?? payload?.id);

    if (!notificationId || selectedSegmentIds.length === 0) {
      return;
    }

    setIsDispatching(true);

    try {
      await instance.post(apiRoutes.notificationManualDispatch, {
        notificationId,
        segmentIds: Array.from(new Set(selectedSegmentIds)),
      });

      setIsConfirmingDispatch(false);
      setSelectedSegmentIds([]);
      dispatch(
        showNotification({
          title: t("success"),
          message: t("notifSendSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      setIsConfirmingDispatch(false);
      handleApiError({ error, t, dispatch });
    } finally {
      setIsDispatching(false);
    }
  };

  const renderTab = () => {
    if (activeTab === ModalNotificationTabs.main) {
      return (
        <PushMainTab
          payload={payload}
          handleData={handleData}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!canEdit}
        />
      );
    }

    if (activeTab === ModalNotificationTabs.locales) {
      return (
        <PushLocalesTab
          activeLocale={activeLocale}
          setActiveLocale={setActiveLocale}
          translation={translation}
          setTranslation={setTranslation}
          onInitialTranslationChange={setInitialTranslation}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          onLoadingChange={setIsLocalesLoading}
          readOnly={!canEdit}
        />
      );
    }

    if (activeTab === ModalNotificationTabs.send) {
      return (
        <PushSendTab
          selectedSegmentIds={selectedSegmentIds}
          onSelectionChange={setSelectedSegmentIds}
          onLoadingChange={setIsSendLoading}
          segmentType={isAutoNotification ? "auto" : "manual"}
        />
      );
    }
    return null;
  };

  return (
    <>
      <Modal onClose={handleCloseClick} size="sm">
        <Modal.Header>
          {payload?.type ? t(`${payload.type}Notification`) : t("notification")}
        </Modal.Header>

        {!isLoaded() ? (
          <ModalSkeleton />
        ) : (
          <>
            {!isConfirmingDispatch && (
              <Modal.Tabs
                tabs={tabs}
                activeTab={activeTab}
                canBeClicked={isEdit}
                onClick={handleTabClick}
              />
            )}

            <Modal.Content
              className={
                isConfirmingDispatch
                  ? styles.confirmationContent
                  : activeTab === ModalNotificationTabs.send
                    ? styles.sendContent
                    : undefined
              }
            >
              {isConfirmingDispatch ? (
                <div className={styles.confirmation}>
                  <h3>{t("confirmNotificationDispatch")}</h3>
                  <p>
                    {t("manualNotificationDispatchConfirmation", {
                      title: payload?.title ?? "",
                      count: selectedSegmentIds.length,
                    })}
                  </p>
                  <dl>
                    <div>
                      <dt>{t("notification")}</dt>
                      <dd>{payload?.title}</dd>
                    </div>
                    <div>
                      <dt>{t("segments")}</dt>
                      <dd>{selectedSegmentIds.length}</dd>
                    </div>
                  </dl>
                </div>
              ) : (
                <div className="out-notif-form">{renderTab()}</div>
              )}
            </Modal.Content>

            {canEdit && (
              <Modal.Footer>
                {isConfirmingDispatch ? (
                  <>
                    <Button
                      isSecondary
                      txt={t("back")}
                      handleClick={() => setIsConfirmingDispatch(false)}
                      disabled={isDispatching}
                    />
                    <Button
                      isGreen
                      className={styles.primaryAction}
                      txt={t("confirm")}
                      handleClick={handleDispatch}
                      isLoad={isDispatching}
                    />
                  </>
                ) : (activeTab === ModalNotificationTabs.locales &&
                    isLocalesLoading) ||
                  (activeTab === ModalNotificationTabs.send &&
                    isSendLoading) ? (
                  <ModalFooterSkeleton />
                ) : (
                  <>
                    <Button
                      isSecondary
                      txt={t("cancel")}
                      handleClick={handleCloseClick}
                    />
                    <Button
                      className={styles.primaryAction}
                      isPrimary={activeTab !== ModalNotificationTabs.send}
                      isGreen={activeTab === ModalNotificationTabs.send}
                      handleClick={handleApply}
                      txt={
                        activeTab === ModalNotificationTabs.send
                          ? isAutoNotification
                            ? t("attach")
                            : t("send")
                          : t("save")
                      }
                      disabled={
                        activeTab === ModalNotificationTabs.send &&
                        isManualNotification &&
                        selectedSegmentIds.length === 0
                      }
                      isLoad={
                        activeTab === ModalNotificationTabs.locales
                          ? isTranslationSaving
                          : isAttaching || isItemFetching
                      }
                    />
                  </>
                )}
              </Modal.Footer>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default NotificationsEditModal;
